/**
 * Die Zustimmung zur Messung in der Anwendung.
 *
 * § 165 Abs 3 TKG 2021 and Art 6 Abs 1 lit a DSGVO: anything stored on or read
 * from the device that is not strictly necessary needs a prior yes. That the
 * person is logged in and pays for the service changes nothing about it — the
 * two cookies that keep them signed in are necessary and are not asked about;
 * a measurement cookie is neither.
 *
 * Three consequences the code honours rather than claims:
 *
 *   1. Nothing is stored and no script is requested until `accept()` runs. The
 *      question itself leaves no trace, so someone who ignores it leaves none.
 *   2. Refusing is one click, exactly like accepting, and the refusal is
 *      remembered — otherwise the question would return on every login and
 *      wear the customer down, which is the pattern the DSB objects to.
 *   3. Withdrawing is as easy as giving (Art 7 Abs 3 DSGVO): `reset()` is on
 *      Einstellungen → Daten und Datenschutz, next to the export.
 *
 * Wortlaut, Nachweis, Frist und Ablage kommen aus `@eua/shared/consent` — ein
 * Cookie auf `easyrechnung.at`, das die Website genauso liest und schreibt.
 * Whoever answered the question on easyrechnung.at has answered it here, and
 * the other way round; one withdrawal ends the measurement on both hosts. The
 * record it replaced lay in this origin's local storage and could not be shared
 * with anything.
 */
import { ref } from 'vue'
import {
  consentCookieAssignment,
  consentCookieRemoval,
  parseStoredConsent,
  readConsentCookie,
  type ConsentState,
} from '@eua/shared'
import { flush, loadAnalytics } from '@/lib/analytics'

/**
 * Module scope, not per component: the banner and the settings page are two
 * views of one decision, and a second `ref` would let one of them show a state
 * the other has already changed.
 */
const state = ref<ConsentState>('unknown')

export function useConsent() {
  /** Reads the stored decision. Runs once, from the banner. */
  function hydrate(): void {
    state.value = read()
    if (state.value !== 'granted') return
    loadAnalytics()
    flush()
  }

  function accept(): void {
    store('granted')
    loadAnalytics()
    // Whatever happened while the dialog was still on screen — a registration,
    // a minute ago — goes out now. See `lib/analytics.ts`.
    flush()
  }

  function decline(): void {
    store('denied')
  }

  /**
   * Puts the customer back in front of the question — on easyrechnung.at too,
   * because it is one record.
   *
   * The scripts already loaded stay loaded — a page that unloads Google is a
   * page that pretends to. What stops is the sending: `track()` reads
   * `window.gtag`, and the next full load has no yes to act on. Nothing new is
   * measured, which is what withdrawal means for a measurement that only ever
   * sends one event, minutes after a subscription and never on its own.
   */
  function reset(): void {
    state.value = 'unknown'
    write(consentCookieRemoval(window.location))
  }

  function store(choice: 'granted' | 'denied'): void {
    state.value = choice
    write(consentCookieAssignment(choice, new Date(), window.location))
  }

  function write(cookie: string): void {
    try {
      document.cookie = cookie
    } catch {
      // A browser that refuses cookies refuses this one too: the decision then
      // holds for this tab and is asked again next time. Never the other way
      // round.
    }
  }

  function read(): ConsentState {
    try {
      return parseStoredConsent(readConsentCookie(document.cookie), new Date())
    } catch {
      return 'unknown'
    }
  }

  return { state, hydrate, accept, decline, reset }
}
